import { Component, Input, OnInit } from '@angular/core';
import { businessBodyData, EnumCardVerificationForm, EnumCardVerificationSection, supplierBodyData } from '../distributor-data/distributor-data.data';
import { BehaviorSubject } from 'rxjs';
import {
  IBusinessDataStep,
  ICardVerificationForm,
  ICardVerificationFormBranch,
  INoteChangeVerification,
  IPayloadRegistrationVerification,
} from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';
import { DistributorTypeEnum } from '@config/enum/distributor.enum';

@Component({
  selector: 'app-business-data',
  templateUrl: './business-data.component.html',
  styleUrls: ['./business-data.component.scss'],
})
export class BusinessDataComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isViewOnly = false;

  bodyDataBusiness: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataSupplier: BehaviorSubject<ICardVerificationFormBranch[]> = new BehaviorSubject<ICardVerificationFormBranch[]>([]);

  dataValue: BehaviorSubject<IBusinessDataStep> = new BehaviorSubject<IBusinessDataStep>({} as IBusinessDataStep);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  revisionNoteData: IPayloadRegistrationVerification;
  revisionType: EnumDistributorRevisionSession;

  get DistributorDetailHeader() {
    return this.detailRegistrationService.getDistributorDetailHeader();
  }

  get ShowBadge() {
    return this.detailRegistrationService.showBadgePendingDetail();
  }

  constructor(public detailRegistrationService: DetailRegistrationDistributorService, private activatedRoute: ActivatedRoute) {
    const statusTab = this.activatedRoute.snapshot.params.tab;
    this.revisionType = this.detailRegistrationService.getTypeRevision(statusTab);
  }

  ngOnInit(): void {
    this.getRevisionNoteData();
    this.getData();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.detailRegistrationService.getSessionRevisionNote(this.revisionType) ?? ({} as IPayloadRegistrationVerification);
  }

  getData() {
    this.detailRegistrationService.getBusinessDataStep().subscribe((value) => {
      if (!value) return;
      this.dataValue.next(value);
      this.generateBodyData(value);
      !this.isViewOnly && this.handleValidationForm();
      this.isLoading.next(false);
    });
  }

  generateBodyData(data: IBusinessDataStep) {
    const { business_information, supplier_information } = data;

    const distributorType = this.DistributorDetailHeader.business_type;
    let _businessBody: ICardVerificationForm[] = businessBodyData('establishment_deed_url');

    // filter out establishment_deed_url for distributor type: individual
    if (distributorType && distributorType === DistributorTypeEnum.INDIVIDUAL) {
      _businessBody = _businessBody.filter((item) => item.key !== 'establishment_deed_url');
    }

    const _business = this.detailRegistrationService.generateBodyData(business_information, _businessBody);
    this.bodyDataBusiness.next(_business);

    const _supplier: ICardVerificationFormBranch[] = [];
    supplier_information.suppliers.forEach((value) => {
      const _supplierBody: ICardVerificationForm[] = supplierBodyData(value.current_data, value.revision_data);
      const _supplierTmp = this.detailRegistrationService.generateBodyData(value, _supplierBody);
      const name = _supplierTmp.find((val: any) => val.key === 'name').value;
      const _data = {
        label: name,
        data: _supplierTmp,
      };
      _supplier.push(_data);
    });

    this.bodyDataSupplier.next(_supplier);
  }

  noteChanged(e: INoteChangeVerification) {
    this.detailRegistrationService.setRevisionData(this.revisionNoteData, e, this.revisionType);
    this.getRevisionNoteData();
  }

  noteValue(type: EnumCardVerificationSection) {
    const { revision_note } = this.revisionNoteData;
    !this.isViewOnly && this.handleValidationForm();
    return revision_note && revision_note[type.toLowerCase() as keyof typeof revision_note];
  }

  handleValidationForm() {
    const { business_information, supplier_information } = this.dataValue.value;
    const { revision_note } = this.revisionNoteData;

    const businessInformationControl = this.form.get('business_information');
    const supplierInformationControl = this.form.get('supplier_information');

    if (businessInformationControl) {
      if (
        this.detailRegistrationService.checkValidationFormStepper(businessInformationControl, business_information) &&
        this.detailRegistrationService.isVerifiedAndRevision(business_information)
      ) {
        businessInformationControl.clearValidators();
      } else {
        businessInformationControl.setValidators([Validators.required]);
        businessInformationControl.setValue(revision_note && revision_note.business_information);
      }
      businessInformationControl.updateValueAndValidity();
    }

    if (supplierInformationControl) {
      if (
        (this.detailRegistrationService.checkValidationFormStepper(supplierInformationControl, supplier_information) &&
          this.detailRegistrationService.isVerifiedAndRevision(supplier_information)) ||
        this.checkNotHaveRevisionSupplier()
      ) {
        supplierInformationControl.clearValidators();
      } else {
        supplierInformationControl.setValidators([Validators.required]);
        supplierInformationControl.setValue(revision_note && revision_note.supplier_information);
      }
      supplierInformationControl.updateValueAndValidity();
    }
  }

  afterActionVerification(event: string | null) {
    if (event === null) {
      this.getRevisionNoteData();
    }
    this.getData();
  }

  checkNotHaveRevisionSupplier() {
    const { supplier_information } = this.dataValue.value;
    if (!supplier_information) return false;
    const checkRevision = supplier_information.suppliers.filter((value) => {
      return !this.detailRegistrationService.checkNotHaveRevision(value);
    });
    return !checkRevision.length;
  }

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
}
